import { useModal } from '@gluedigital/modal';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useFeedback } from 'src/hooks/api';
import { useIsBubbleWidget } from 'src/hooks/useIsBubbleWidget';
import { useIsEmbedded } from 'src/hooks/useIsEmbedded';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { useSourcesStore } from 'src/hooks/useSourcesStore';
import { handleAddInternetSources, handleAddSources } from 'src/store/actions/sources';
import { BotMessage, FeedbackEnum, SourceURLFeedback, UserMessage } from 'src/types';
import { useAppDispatch } from 'universal-scripts';

interface BotMessageFooterProps {
	typing?: boolean;
	message: BotMessage;
	replyTo?: UserMessage;
}

const BotMessageFooter = ({ message, replyTo, typing = false }: BotMessageFooterProps) => {
	const isBubbleWidget = useIsBubbleWidget();
	const isEmbedded = useIsEmbedded();
	const hideHostChrome = isBubbleWidget || isEmbedded;
	const isMobile = useIsMobile();
	const { messageId } = useSourcesStore();

	const dispatch = useAppDispatch();
	const { id, sources, internetSources } = message;

	const handleShowSources = () => {
		dispatch(handleAddSources(id, sources));
		dispatch(handleAddInternetSources(id, internetSources));
	};

	const [feedback, setFeedback] = useState<FeedbackEnum | null>(message.feedback);
	const modal = useModal();

	const postFeedback = useFeedback({
		questionId: id
	}).post;
	const deleteFeedback = useFeedback({ questionId: id }).remove;

	const handleShare = () => modal.show('share-modal', { message, replyTo });

	const handleLike = () => {
		if (feedback !== FeedbackEnum.LIKE) {
			handleShowSources();
			modal.show('feedback-modal', {
				handleFeedback,
				messageId: id,
				feedback: FeedbackEnum.LIKE
			});
		} else {
			deleteFeedback();
			setFeedback(null);
		}
	};

	const handleDislike = () => {
		if (feedback !== FeedbackEnum.DISLIKE) {
			handleShowSources();
			modal.show('feedback-modal', {
				handleFeedback,
				messageId: id,
				feedback: FeedbackEnum.DISLIKE
			});
		} else {
			deleteFeedback();
			setFeedback(null);
		}
	};

	const handleFeedback = (
		nextFeedback: FeedbackEnum,
		comment?: string,
		sourceFeedback?: SourceURLFeedback[],
		internetSourceFeedback?: SourceURLFeedback[]
	) => {
		setFeedback(nextFeedback);
		postFeedback(nextFeedback === FeedbackEnum.LIKE, comment, sourceFeedback, internetSourceFeedback);
	};

	const isSelected = id === messageId;
	const usedSources = sources.filter((s) => s.position);
	const usedInternetSources = internetSources.filter((s) => s.position);
	const totalSourcesAmount = usedSources.length + usedInternetSources.length;

	return (
		<div className="buttons">
			{!hideHostChrome && (
				<div className="left">
					<button
						disabled={typing}
						onClick={handleLike}
						className={`icon icon-hand-up ${feedback === FeedbackEnum.LIKE ? 'active' : ''}`}
					/>
					<button
						disabled={typing}
						onClick={handleDislike}
						className={`icon icon-hand-down ${feedback === FeedbackEnum.DISLIKE ? 'active' : ''}`}
					/>
				</div>
			)}
			{!!totalSourcesAmount && !isMobile && (
				<button className={`source-button ${isSelected ? ' selected' : ''}`} onClick={handleShowSources}>
					<FormattedMessage values={{ n: totalSourcesAmount }} id={`source-button.${isSelected ? 'selected' : 'not-selected'}`} />
				</button>
			)}

			<div className="right">
				{!hideHostChrome && (
					<button disabled={typing} onClick={handleShare} className="share-button">
						<span className="icon icon-share" />
					</button>
				)}
			</div>
		</div>
	);
};

export default BotMessageFooter;
